import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, EyeToggle } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isSetup = searchParams.get("setup") === "true";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (!token) { toast.error("Invalid reset link"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/"), 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally { setLoading(false); }
  };

  const title = (() => {
    if (success) return "You're all set!";
    if (isSetup) return "Create your login";
    return "Set new password";
  })();

  return (
    <AuthShell
      eyebrow={isSetup ? "Account Setup" : "Account Recovery"}
      title={title}
      subtitle={isSetup && !success ? "Choose a strong password to secure your account" : undefined}
    >
      {!success ? (
        <form onSubmit={handleSubmit} className="space-y-3" autoComplete="on">
          {/* Hidden username field — required for Chrome to show Suggest Strong Password */}
          <input type="hidden" autoComplete="username" value="reset-password" readOnly />
          <div className="relative">
            <input type={showPassword ? "text" : "password"} placeholder="New password" value={newPassword}
              onChange={e => setNewPassword(e.target.value)} required
              autoComplete="new-password"
              id="new-password"
              name="new-password"
              className="input-flat h-11 pr-11" />
            <EyeToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />
          </div>
          <div className="relative">
            <input type={showPassword ? "text" : "password"} placeholder="Confirm new password" value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} required
              autoComplete="new-password"
              id="confirm-new-password"
              name="confirm-new-password"
              className="input-flat h-11 pr-11" />
          </div>
          <div className="p-3 rounded-[10px] bg-mint border border-[#cfe9db] text-xs text-equal-dark space-y-1">
            <p className="font-semibold">Requirements:</p>
            <p>8+ characters · One uppercase · One number</p>
          </div>
          <button type="submit" disabled={loading}
            className="btn-primary w-full h-11">
            {loading ? "Saving..." : (isSetup ? "Create my login" : "Reset Password")}
          </button>
          <p className="text-center">
            <button type="button" onClick={() => navigate("/")} className="text-xs text-mute hover:text-ink transition-colors">Back to login</button>
          </p>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="w-12 h-12 rounded-[10px] flex items-center justify-center bg-equal">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <p className="text-mute text-sm">Your password has been reset. Redirecting to login...</p>
        </div>
      )}
    </AuthShell>
  );
}
